/**
 * Column definitions for the publishing plan.
 *
 * The same module shape as `ambassadors/table.ts` and the three before it, with
 * one difference that earns the `.tsx`: two of these columns render marks rather
 * than words, so `buildCells` returns nodes.
 *
 * When is not in here. It is the identity column — always shown, always first,
 * two lines — and a post has no name of its own, so the date and the time are
 * what identify it.
 *
 * ## Why `Cell.node` exists
 *
 * The Channels column is six dots in a fixed order, and the alignment down the
 * column *is* the column: Facebook's dot in the same horizontal position on
 * every row is what makes "which posts touch Facebook" a glance. No arrangement
 * of text reproduces that, so rather than special-case this screen inside the
 * shared table, `Cell` gained one narrow escape hatch — see `DataTable`. The
 * Live post column uses it for the second honest reason: a link is a link.
 */
import type {
  Cell as SharedCell,
  ColumnFilters as SharedColumnFilters,
  ColumnMeta,
} from '@/shared/components/DataTable'
import {
  CHANNELS,
  CONTENT_TYPES,
  POST_STATUSES,
  postStatusStyle,
  type Channel,
  type Post,
} from '@/types/posts'
import { ChannelDots, soloChannelName } from './ChannelDots'

export type ColumnKey = 'channels' | 'type' | 'status' | 'owner' | 'linked' | 'live'

export const COLUMN_META: Record<ColumnKey, ColumnMeta> = {
  /** Six slots in a constant order. The empty ones are the feature. */
  channels: { label: 'Channels', width: 1 },
  type: { label: 'Type', width: 0.9 },
  status: { label: 'Status', width: 0.8 },
  owner: { label: 'Owner', width: 0.9 },
  /** The event, ambassadors and partners a post is about. */
  linked: { label: 'Linked to', width: 1.4 },
  /** The published URL, and how much creative is attached. */
  live: { label: 'Live post', width: 0.9 },
}

export const COLUMN_KEYS = Object.keys(COLUMN_META) as ColumnKey[]

/** The design's set, in the design's order. */
export const DEFAULT_COLUMNS: ColumnKey[] = ['channels', 'type', 'status', 'owner', 'linked', 'live']

/** `post` is the identity column: the subject, per decision 44. */
export type SortKey = ColumnKey | 'when' | 'post'

export const FILTERABLE_COLUMNS: ColumnKey[] = ['channels', 'type', 'status', 'owner']

export type ColumnFilters = SharedColumnFilters<ColumnKey>
export type Cell = SharedCell<ColumnKey>

/**
 * The values a filter compares against, for one post and one column.
 *
 * Plural, as on the ambassador table and for the same reason: a post goes to
 * several channels at once, and one that goes to Instagram and Facebook has to
 * appear under both.
 */
export function filterValues(p: Post, key: ColumnKey): string[] {
  switch (key) {
    case 'channels':
      return p.channels
    case 'type':
      return [p.type]
    case 'status':
      return [p.status]
    case 'owner':
      return [p.owner || 'unassigned']
    default:
      return []
  }
}

export function passesFilters(p: Post, filters: ColumnFilters): boolean {
  return FILTERABLE_COLUMNS.every((key) => {
    const selected = filters[key]
    if (!selected || selected.length === 0) return true
    const values = filterValues(p, key)
    return selected.some((v) => values.includes(v))
  })
}

/** The channels as words — what the tooltip and the phone card say. */
const channelText = (channels: Channel[]) =>
  channels.length ? channels.join(', ') : 'no channel yet'

export function buildCells(p: Post, columns: ColumnKey[], linkedLabel: string): Cell[] {
  return columns.map((key): Cell => {
    switch (key) {
      case 'channels': {
        const solo = soloChannelName(p.channels)
        return {
          key,
          badge: false,
          text: channelText(p.channels),
          title: channelText(p.channels),
          node: (
            <>
              <ChannelDots channels={p.channels} />
              {solo && <div className="pub__solo">{solo}</div>}
            </>
          ),
        }
      }
      case 'type':
        return { key, badge: false, text: p.type }
      case 'status': {
        const st = postStatusStyle(p.status)
        return { key, badge: true, text: st.label, bg: st.bg, color: st.color }
      }
      case 'owner':
        return { key, badge: false, text: p.owner || 'unassigned' }
      case 'linked':
        return { key, badge: false, text: linkedLabel || '—', title: linkedLabel || undefined }
      case 'live': {
        const attached = p.assets.length
        return {
          key,
          badge: false,
          text: [p.link ? 'published' : '', attached ? `${attached} attached` : '']
            .filter(Boolean)
            .join(' · ') || '—',
          node:
            p.link || attached ? (
              <span className="pub__last">
                {p.link && (
                  // Opening the post must not also open the editor behind it.
                  <a
                    href={p.link}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    open ↗
                  </a>
                )}
                {/* The attachment count belongs in the list, not only in the
                    editor: reusable creative is findable only if you can see
                    from the table which posts have any. */}
                {attached > 0 && (
                  <span className="pub__assets" title={`${attached} attached materials`}>
                    📎 {attached}
                  </span>
                )}
              </span>
            ) : undefined,
        }
      }
    }
  })
}

/**
 * Sort key for one post.
 *
 * Status sorts by workflow position, channels by how many a post goes to — the
 * useful question there is "which posts are going everywhere", not which begin
 * with F. Blanks return null and sink either way: an unscheduled post is not the
 * next one out.
 */
export function sortValue(p: Post, sortBy: SortKey, linkedLabel: string): string | number | null {
  switch (sortBy) {
    case 'post':
      // Undated ideas are the normal state (decision 20), so the plan is named
      // by its subject and sorts by it.
      return p.subject.toLowerCase() || null
    case 'channels':
      return p.channels.length || null
    case 'type':
      return CONTENT_TYPES.indexOf(p.type)
    case 'status':
      return POST_STATUSES.findIndex((s) => s.key === p.status)
    case 'owner':
      return p.owner.toLowerCase() || null
    case 'linked':
      return linkedLabel.toLowerCase() || null
    case 'live':
      return p.link ? 1 : p.assets.length ? 0 : null
    default:
      // Date and time as one key, so two posts on the same day sort by the hour.
      return p.date ? `${p.date} ${p.time || '00:00'}` : null
  }
}

export function sortPosts(
  rows: Post[],
  sortBy: SortKey,
  dir: 'asc' | 'desc',
  linkedLabel: (p: Post) => string,
): Post[] {
  const sign = dir === 'asc' ? 1 : -1
  const when = (p: Post) => (p.date ? `${p.date} ${p.time || '00:00'}` : '')
  return [...rows].sort((a, b) => {
    const av = sortValue(a, sortBy, linkedLabel(a))
    const bv = sortValue(b, sortBy, linkedLabel(b))
    if (av === null && bv === null) return when(a).localeCompare(when(b))
    if (av === null) return 1
    if (bv === null) return -1
    if (av < bv) return -sign
    if (av > bv) return sign
    // When is the tiebreaker: a plan is read in date order.
    return when(a).localeCompare(when(b))
  })
}

/** Every channel, for the header filter — including ones nothing uses yet. */
export const CHANNEL_OPTIONS = CHANNELS.map((c) => ({ value: c, label: c }))
